import React from 'react';
import {
  ChatBubbleLeftRightIcon,
  PhoneIcon,
  DocumentTextIcon,
  EyeIcon,
  PaperAirplaneIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';

export const QuickSmsGuidePage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-6 flex items-center justify-between">
          <div>
            <div className="flex items-center space-x-3">
              <ChatBubbleLeftRightIcon className="w-8 h-8 text-[#FF5722]" />
              <h1 className="text-3xl font-bold text-gray-900">
                How to Send a Quick SMS
              </h1>
            </div>
            <p className="mt-2 text-sm text-gray-600 max-w-2xl">
              Follow this step‑by‑step guide to send a one‑off Quick SMS to one or more recipients
              using the live backend integration.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
        {/* High level steps */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow-sm p-4">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-1">
              Step 1
            </h2>
            <p className="text-base font-medium text-gray-900">
              Open the Quick SMS page
            </p>
            <p className="mt-2 text-sm text-gray-600">
              Use the navigation to go to <span className="font-medium">Quick SMS</span>.
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-1">
              Step 2
            </h2>
            <p className="text-base font-medium text-gray-900">
              Select Sender ID & recipients
            </p>
            <p className="mt-2 text-sm text-gray-600">
              Choose an approved Sender ID and add one or more phone numbers.
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-1">
              Step 3
            </h2>
            <p className="text-base font-medium text-gray-900">
              Write or generate your message
            </p>
            <p className="mt-2 text-sm text-gray-600">
              Type the SMS or use templates and the AI SMS Assistant to generate content.
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-1">
              Step 4
            </h2>
            <p className="text-base font-medium text-gray-900">
              Preview & send
            </p>
            <p className="mt-2 text-sm text-gray-600">
              Preview exactly what will be sent, then confirm to send via the backend.
            </p>
          </div>
        </div>

        {/* Step 1 & 2: open page and select sender/recipients */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center space-x-3 mb-4">
              <ChatBubbleLeftRightIcon className="w-7 h-7 text-[#FF5722]" />
              <h3 className="text-lg font-semibold text-gray-900">1. Open the Quick SMS page</h3>
            </div>
            <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
              <li>
                After logging in, navigate to <span className="font-medium">Quick SMS</span> from
                the main sidebar.
              </li>
              <li>
                You&apos;ll see a single‑screen layout with sections for Sender ID, recipients,
                message content, AI assistant and action buttons.
              </li>
            </ol>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center space-x-3 mb-4">
              <PhoneIcon className="w-7 h-7 text-[#FF5722]" />
              <h3 className="text-lg font-semibold text-gray-900">
                2. Select Sender ID and add recipients
              </h3>
            </div>
            <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
              <li>
                Under <span className="font-medium">Select Sender I.D.</span>, pick a Sender ID from
                the dropdown. These values are loaded from the backend using your user ID.
              </li>
              <li>
                In <span className="font-medium">Add recipients</span>, type a phone number in the
                format <span className="font-mono">263712345678</span> and press{' '}
                <span className="font-medium">Enter</span> or click the plus button.
              </li>
              <li>
                The platform will:
                <ul className="list-disc list-inside ml-5 mt-1 text-gray-600">
                  <li>clean the number (remove spaces, brackets and dashes)</li>
                  <li>
                    validate that it starts with <span className="font-mono">263</span> and has
                    exactly 12 digits
                  </li>
                  <li>reject duplicates already in the recipients list</li>
                </ul>
              </li>
              <li>
                Any validation issues (missing number, wrong format, duplicate) are shown inline
                under the input in red text.
              </li>
              <li>
                Review the <span className="font-medium">Recipients</span> box below: it displays up
                to 5 phone numbers with a total count. If more than 5 are added, you&apos;ll see a
                note like &quot;... and N more recipient(s)&quot;.
              </li>
            </ol>
            <p className="mt-4 text-xs text-gray-500">
              These validation rules are enforced consistently across the platform and backed by the
              same phone‑number logic described in the backend documentation.
            </p>
          </div>
        </div>

        {/* Step 3: compose message */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center space-x-3 mb-4">
              <DocumentTextIcon className="w-7 h-7 text-[#FF5722]" />
              <h3 className="text-lg font-semibold text-gray-900">3. Write your Quick SMS</h3>
            </div>
            <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
              <li>
                Under <span className="font-medium">Add SMS message</span>, optionally select a
                saved <span className="font-medium">Template</span> to pre‑fill the message body.
                Templates are loaded from the backend for your account.
              </li>
              <li>
                Type or edit your SMS text in the message box. Keep it concise and focused on a
                single action.
              </li>
              <li>
                Use the <span className="font-medium">Message Variables</span> dropdown to insert
                placeholders such as <span className="font-mono">{'{NAME}'}</span>,{' '}
                <span className="font-mono">{'{PHONE}'}</span>,{' '}
                <span className="font-mono">{'{UNSUBSCRIBE}'}</span>, etc., to personalize or add
                compliance information.
              </li>
              <li>
                Watch the <span className="font-medium">Total symbols in SMS message</span> panel:
                it shows character count and the estimated number of SMS segments (
                <span className="font-mono">160</span> characters per segment).
              </li>
            </ol>
            <p className="mt-4 text-xs text-gray-500">
              The number of segments multiplied by the number of recipients determines how many
              credits the backend will consume.
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center space-x-3 mb-4">
              <SparklesIcon className="w-7 h-7 text-[#FF5722]" />
              <h3 className="text-lg font-semibold text-gray-900">
                4. (Optional) Use the AI SMS Assistant
              </h3>
            </div>
            <p className="text-sm text-gray-700 mb-3">
              If you&apos;re not sure what to write, the AI SMS Assistant can generate a tailored
              message for you.
            </p>
            <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
              <li>
                In the <span className="font-medium">AI SMS Assistant</span> panel, describe your
                intent in <span className="font-medium">Message Overview</span> (for example,
                &quot;Notify customers about a planned outage&quot;).
              </li>
              <li>
                Choose a <span className="font-medium">Message Goal</span> (Promotional, Informational
                Update, Event Reminder, Customer Service, etc.) and a{' '}
                <span className="font-medium">Tone</span> (Friendly, Professional, Casual, Formal,
                Urgent, Assertive).
              </li>
              <li>
                Optionally select message variables to include in the generated output.
              </li>
              <li>
                Click <span className="font-medium">Generate Message</span>. The client calls the
                backend AI endpoint, and the response is shown as an AI‑generated SMS.
              </li>
              <li>
                Click <span className="font-medium">Use Generated Message &amp; Preview</span> to
                copy it into your main message field and automatically switch to Preview mode.
              </li>
            </ol>
            <p className="mt-4 text-xs text-gray-500">
              Always review AI‑generated content for accuracy and compliance before sending to real
              customers.
            </p>
          </div>
        </div>

        {/* Step 4: preview & send */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center space-x-3 mb-4">
            <EyeIcon className="w-7 h-7 text-[#FF5722]" />
            <h3 className="text-lg font-semibold text-gray-900">5. Preview and send</h3>
          </div>
          <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
            <li>
              When you&apos;re happy with the recipients and message content, click{' '}
              <span className="font-medium">Preview SMS</span>. The page switches into a dedicated
              preview view (`PreviewSMSPage`).
            </li>
            <li>
              In the preview, confirm:
              <ul className="list-disc list-inside ml-5 mt-1 text-gray-600">
                <li>the exact message body that will be sent</li>
                <li>the list of recipients (again limited visually but with an accurate total)</li>
                <li>the current Sender ID</li>
              </ul>
            </li>
            <li>
              If you need to change anything, click <span className="font-medium">Back/Edit</span>{' '}
              in the preview to return to the main Quick SMS form.
            </li>
            <li>
              To send, click <span className="font-medium">Send Now</span>. The frontend calls the
              backend <span className="font-mono">sendQuickSms</span> API with your Sender ID,
              message body and recipients list.
            </li>
            <li>
              The backend responds with how many recipients were queued, sent and marked as invalid.
              This is shown in an alert similar to:
              <span className="block mt-1 font-mono text-xs text-gray-700">
                Queued 10 recipient(s). Sent: 10. Invalid: 0.
              </span>
            </li>
          </ol>
          <p className="mt-4 text-xs text-gray-500">
            If there are errors (for example, missing Sender ID or network issues), the platform
            shows a clear error message so you can correct the problem and try again.
          </p>
        </div>
      </div>
    </div>
  );
};

export default QuickSmsGuidePage;


